import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { useLanguage } from '../lib/language';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import Sidebar from '../components/Sidebar';
import { motion } from 'framer-motion';
import axios from 'axios';
import {
  Search,
  Plus,
  Loader2,
  Users,
  Mail,
  Phone,
  UserPlus,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ClientsPage() {
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    notes: '',
  });

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${API}/clients`);
      setClients(response.data);
    } catch (error) {
      toast.error('Nie udalo sie zaladowac klientow'); {/* t('clients.loadError') */}
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error('Nazwa klienta jest wymagana'); {/* t('clients.nameRequired') */}
      return;
    }
    setCreating(true);
    try {
      const response = await axios.post(`${API}/clients`, form);
      setClients([response.data, ...clients]);
      setDialogOpen(false);
      setForm({ name: '', email: '', phone: '', notes: '' });
      toast.success('Klient zostal dodany'); {/* t('clients.created') */}
    } catch (error) {
      toast.error('Nie udalo sie dodac klienta'); {/* t('clients.createError') */}
    } finally {
      setCreating(false);
    }
  };

  const filteredClients = clients.filter(c =>
    c.name?.toLowerCase().includes(search.toLowerCase()) ||
    c.email?.toLowerCase().includes(search.toLowerCase()) ||
    c.phone?.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Sidebar activePath="/clients" />

      {/* Main Content */}
      <main className="lg:ml-64 p-6 lg:p-10">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold">Klienci {/* t('clients.title') */}</h1>
            <p className="text-muted-foreground mt-1">
              {filteredClients.length} klientow {/* t('clients.count', { count }) */}
            </p>
          </div>
          <Button onClick={() => setDialogOpen(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Nowy klient {/* t('clients.new') */}
          </Button>
        </div>

        {/* Search */}
        <div className="relative mb-8">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Szukaj klientow..." /* t('clients.searchPlaceholder') */
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 h-12 max-w-md"
          />
        </div>

        {/* Clients Table */}
        {filteredClients.length > 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <Card className="bg-card border-border">
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="pl-6">Klient {/* t('clients.columns.client') */}</TableHead>
                      <TableHead>Kontakt {/* t('clients.columns.contact') */}</TableHead>
                      <TableHead className="text-center">Selekcje {/* t('clients.columns.selections') */}</TableHead>
                      <TableHead className="text-center">Zamowienia {/* t('clients.columns.orders') */}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredClients.map((client, idx) => (
                      <motion.tr
                        key={client.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.2, delay: idx * 0.03 }}
                        className="border-b transition-colors hover:bg-muted/50 cursor-pointer"
                        onClick={() => navigate(`/clients/${client.id}`)}
                      >
                        <TableCell className="pl-6 py-4">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                              <span className="text-sm font-semibold text-primary">
                                {client.name?.charAt(0)?.toUpperCase() || '?'}
                              </span>
                            </div>
                            <Link
                              to={`/clients/${client.id}`}
                              className="font-medium hover:text-primary transition-colors"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {client.name}
                            </Link>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="space-y-1">
                            {client.email && (
                              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                <Mail className="w-3.5 h-3.5" />
                                {client.email}
                              </div>
                            )}
                            {client.phone && (
                              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                <Phone className="w-3.5 h-3.5" />
                                {client.phone}
                              </div>
                            )}
                            {!client.email && !client.phone && (
                              <span className="text-sm text-muted-foreground">-</span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-center">
                          <span className="text-sm font-medium">
                            {client.selections_count ?? client._count?.selections ?? 0}
                          </span>
                        </TableCell>
                        <TableCell className="text-center">
                          <span className="text-sm font-medium">
                            {client.orders_count ?? client._count?.orders ?? 0}
                          </span>
                        </TableCell>
                      </motion.tr>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </motion.div>
        ) : (
          <Card className="bg-card border-border">
            <CardContent className="p-12 text-center">
              <Users className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
              <h3 className="text-lg font-medium mb-2">
                {search
                  ? 'Nie znaleziono klientow' /* t('clients.noResults') */
                  : 'Brak klientow' /* t('clients.empty') */
                }
              </h3>
              <p className="text-sm text-muted-foreground mb-4">
                {search
                  ? 'Sprobuj zmienic kryteria wyszukiwania' /* t('clients.noResultsHint') */
                  : 'Dodaj pierwszego klienta, aby rozpoczac' /* t('clients.emptyHint') */
                }
              </p>
              {!search && (
                <Button onClick={() => setDialogOpen(true)} className="mt-2">
                  <UserPlus className="w-4 h-4 mr-2" />
                  Dodaj klienta {/* t('clients.addFirst') */}
                </Button>
              )}
            </CardContent>
          </Card>
        )}
      </main>

      {/* New Client Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Nowy klient {/* t('clients.new') */}</DialogTitle>
            <DialogDescription>
              Dodaj nowego klienta do swojej bazy {/* t('clients.newDescription') */}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Imie i nazwisko {/* t('clients.form.name') */}</Label>
              <Input
                id="name"
                placeholder="Jan Kowalski"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email {/* t('clients.form.email') */}</Label>
              <Input
                id="email"
                type="email"
                placeholder="jan@example.com"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone">Telefon {/* t('clients.form.phone') */}</Label>
              <Input
                id="phone"
                type="tel"
                placeholder="+48 123 456 789"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="notes">Notatki {/* t('clients.form.notes') */}</Label>
              <Textarea
                id="notes"
                placeholder="Dodatkowe informacje o kliencie..." /* t('clients.form.notesPlaceholder') */
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                rows={3}
              />
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setDialogOpen(false)}
              >
                Anuluj {/* t('common.cancel') */}
              </Button>
              <Button type="submit" disabled={creating}>
                {creating && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Dodaj klienta {/* t('clients.add') */}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
